<script setup>
  import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import * as SDCBarcode from "scandit-web-datacapture-barcode";
  import * as SDCCore from "scandit-web-datacapture-core";

  defineOptions({
    name: "ComponentBarcodeScanner",
    inheritAttrs: false,
  });

  const props = defineProps({
    // we have only a license for QR, DataMatrix, Code39 and Code128
    barcodeFormats: {
      type: Array,
      default() {
        return [SDCBarcode.Symbology.DataMatrix, SDCBarcode.Symbology.Code39, SDCBarcode.Symbology.Code128];
      },
    },
    paused: {
      type: Boolean,
      default: false,
    },
    usePatientText: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(["camera-on", "error", "detect"]);

  const refScannerContainer = ref(null);

  let barcodeCapture = null;
  let camera = null;

  const hasLowQualityCamera = ref(false);
  const hasReachedTimeout = ref(false);

  watch(
    () => props.paused,
    (shouldPause, isPaused) => {
      if (shouldPause === isPaused) return;

      if (shouldPause && barcodeCapture) {
        camera.switchToDesiredState(SDCCore.FrameSourceState.Off);
        barcodeCapture.setEnabled(false);
      } else if (!shouldPause && barcodeCapture) {
        camera.switchToDesiredState(SDCCore.FrameSourceState.On);
        barcodeCapture.setEnabled(true);
      }
    },
  );

  watch(
    () => props.barcodeFormats,
    (newBarcodeFormats, oldBarcodeFormats) => {
      if (JSON.stringify(newBarcodeFormats) !== JSON.stringify(oldBarcodeFormats)) {
        startBarcodeScanner(newBarcodeFormats);
      }
    },
    {deep: true},
  );

  onMounted(async () => {
    try {
      await SDCCore.configure({
        licenseKey: import.meta.env.VITE_SCANDIT_LICENSE_KEY,
        logLevel: SDCCore.Logger.Level.Quiet,
        libraryLocation: import.meta.env.DEV
          ? import.meta.url.split("/re")[0] + "/node_modules/scandit-web-datacapture-barcode/build/engine/"
          : "/build/scandit-sdk/",
        moduleLoaders: [SDCBarcode.barcodeCaptureLoader()],
      });

      await startBarcodeScanner(props.barcodeFormats);
      emit("camera-on");
    } catch (error) {
      onError(error);
    }
  });

  onBeforeUnmount(() => {
    if (barcodeCapture) {
      barcodeCapture.setEnabled(false);
      barcodeCapture.context.frameSource.switchToDesiredState(SDCCore.FrameSourceState.Off);
    }
  });

  const codeType = computed(() => {
    if (props.barcodeFormats.length === 1 && props.barcodeFormats[0] === SDCBarcode.Symbology.DataMatrix) {
      return "DATAMATRIX";
    }

    if (props.barcodeFormats.length === 1 && props.barcodeFormats[0] !== SDCBarcode.Symbology.DataMatrix) {
      return "BARCODE";
    }

    if (props.barcodeFormats.length > 1 && !props.barcodeFormats.includes(SDCBarcode.Symbology.DataMatrix)) {
      return "BARCODE";
    }

    return "MIXED";
  });

  const startBarcodeScanner = async (barcodeFormats) => {
    try {
      const container = refScannerContainer.value;

      const view = new SDCCore.DataCaptureView();

      const context = await SDCCore.DataCaptureContext.create();

      context.addListener({
        didChangeStatus: (context, contextStatus) => {
          if (!contextStatus.isValid) {
            onError({name: "InvalidLicenseKey"});
          }
        },
      });

      await view.setContext(context);

      const settings = new SDCBarcode.BarcodeCaptureSettings();
      settings.enableSymbologies(barcodeFormats);
      settings.codeDuplicateFilter = 1000;
      settings.getProperty();

      barcodeCapture = await SDCBarcode.BarcodeCapture.forContext(context, settings);

      const feedback = new SDCBarcode.BarcodeCaptureFeedback();
      feedback.success = new SDCCore.Feedback(null, null);

      await barcodeCapture.setFeedback(feedback);

      barcodeCapture.addListener({
        didScan: (barcodeCapture, session) => {
          const recognizedBarcodes = session.newlyRecognizedBarcodes;
          emit("detect", recognizedBarcodes);
        },
      });

      view.logoStyle = SDCCore.LogoStyle.Minimal;

      const cameraSettings = SDCBarcode.BarcodeCapture.recommendedCameraSettings;
      cameraSettings.preferredResolution = SDCCore.VideoResolution.FullHD;

      camera = SDCCore.Camera.default;

      if (camera) {
        await camera.applySettings(cameraSettings);
        await camera.setMirrorImageEnabled(false);
      }

      await context.setFrameSource(camera);

      view.connectToElement(container);
      await camera.switchToDesiredState(SDCCore.FrameSourceState.On);

      const width = camera.currentResolution.width;
      const height = camera.currentResolution.height;
      hasLowQualityCamera.value = Math.max(width, height) < 1920 || Math.min(width, height) < 1080;

      setTimeout(async function () {
        hasReachedTimeout.value = true;
        await nextTick();
        scrollToScanner();
      }, 30 * 1000);

      await nextTick();
      scrollToScanner();
    } catch (error) {
      onError(error);
    }
  };

  const onError = (event = null) => {
    const error = {
      name: event?.name ?? "UnknownError",
      message: null,
    };

    if (["NotAllowedError"].includes(event?.name)) {
      error.message =
        "Bitte verwenden Sie ein Gerät mit einer Kamera oder erlauben den Zugriff auf diese in den Browser- bzw. Systemeinstellungen.";
    } else if (event?.name === "InvalidLicenseKey") {
      error.message = "Es ist ein Fehler aufgetreten. Bitte kontaktieren Sie den Kundensupport.";
    } else {
      error.message = "Es ist ein Fehler aufgetreten. Bitte laden Sie die Seite neu und versuchen es erneut.";
    }

    emit("error", error);
  };

  const scrollToScanner = () => {
    refScannerContainer.value?.scrollIntoView({behavior: "smooth"});
  };
</script>

<template>
  <div class="relative z-0 size-full">
    <component-alert v-if="hasLowQualityCamera" type="warning" :test-id="getTestId('barcode-scanner')">
      Die Auflösung Ihrer Kamera liegt unter 1080p. Das kann das Scannen von
      <template v-if="codeType === 'DATAMATRIX'">BMP-Datamatrix-Codes</template>
      <template v-else-if="codeType === 'BARCODE'">Strichcodes</template>
      <template v-else>Datamatrix-Codes (BMP) oder Strichcodes</template>
      erschweren oder unmöglich machen.
    </component-alert>

    <component-alert v-if="hasReachedTimeout" type="info" :test-id="getTestId('barcode-scanner')">
      <span class="font-bold">
        Haben Sie Schwierigkeiten, den
        <template v-if="codeType === 'DATAMATRIX'">BMP</template>
        <template v-else-if="codeType === 'BARCODE'">Strichcode</template>
        <template v-else>Datamatrix-Code (BMP) oder Strichcode</template>
        mit Ihrer Kamera zu erfassen?
      </span>
      <div v-if="usePatientText">
        Halten Sie den Code möglichst ruhig und nah vor die Kamera, damit dieser optimal erfasst werden kann.
      </div>
      <ul v-else class="ms-6 list-disc">
        <li>Halten Sie den Code möglichst ruhig und nah vor die Kamera, damit dieser optimal erfasst werden kann.</li>
        <li>
          In Notebooks integrierte Webcams haben häufig Schwierigkeiten mit den feinen Linien des Codes. Eine externe
          Webcam liefert in der Regel deutlich bessere Ergebnisse.
        </li>
        <li>Am zuverlässigsten funktioniert ein externer Handscanner, der über USB angeschlossen wird.</li>
      </ul>
    </component-alert>

    <div ref="refScannerContainer" class="size-full" :class="$attrs.class" />

    <div class="absolute top-0 left-0 size-full">
      <slot />
    </div>
  </div>
</template>
